import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { Box, Grid, Paper, Typography } from '@material-ui/core';
import CameraIcon from '@material-ui/icons/Camera';
import StopIcon from '@material-ui/icons/Stop';
import SaveIcon from '@material-ui/icons/Save';
import ClearIcon from '@material-ui/icons/Clear';
import CloseIcon from '@material-ui/icons/Close';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import { PHOTO_DEFAULT_VALUES } from '../../constants/personPhotoFormConstants';
import { uploadPersonPhoto } from '../../services/userService';
import PersonPhotoMetaFields from './PersonPhotoMetaFields';

const PersonWebcamCapture = forwardRef(
  ({ personId, hasDefaultPhoto, forceMark = false, onSaved, onClose }, ref) => {
    const dispatch = useDispatch();
    const canEditPerson = getFullPermissions('globals', 'People', 'any').Edit;
    const videoRef = useRef(null);
    const photoRef = useRef(null);
    const streamRef = useRef(null);
    const isMountedRef = useRef(true);
    const hasPhotoRef = useRef(false);
    const [hasVideo, setHasVideo] = useState(false);
    const [hasPhoto, setHasPhoto] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const methods = useForm({
      mode: 'onSubmit',
      defaultValues: { ...PHOTO_DEFAULT_VALUES, IsMark: forceMark },
    });

    hasPhotoRef.current = hasPhoto;

    useImperativeHandle(ref, () => ({ isDirty: () => hasPhotoRef.current }), []);

    const startCamera = useCallback(() => {
      if (!navigator.mediaDevices?.getUserMedia) {
        dispatch(
          notify('Camera access requires a secure (HTTPS) connection or localhost.', 'error')
        );
        return;
      }

      setHasVideo(true);
      navigator.mediaDevices
        .getUserMedia({ video: { width: 720, height: 480 } })
        .then((stream) => {
          if (!isMountedRef.current) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }

          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
        })
        .catch((err) => {
          if (!isMountedRef.current) return;

          setHasVideo(false);
          dispatch(notify(err?.message || 'Unable to access the camera.', 'error'));
        });
    }, [dispatch]);

    const stopStream = useCallback(() => {
      const stream = videoRef.current?.srcObject;

      stream?.getVideoTracks().forEach((track) => track.stop());

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setHasVideo(false);
    }, []);

    const takePhoto = useCallback(() => {
      const width = 414;
      const height = width / (16 / 9);
      const canvas = photoRef.current;

      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0, width, height);

      setHasPhoto(true);
      stopStream();
    }, [stopStream]);

    const stopVideo = useCallback(() => {
      stopStream();
    }, [stopStream]);

    const clearPhoto = useCallback(() => {
      const canvas = photoRef.current;

      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
      setHasPhoto(false);
      methods.reset({ ...PHOTO_DEFAULT_VALUES, IsMark: forceMark });
    }, [methods, forceMark]);

    const saveSnap = methods.handleSubmit(async (data) => {
      const canvas = photoRef.current;

      await new Promise((resolve) => {
        canvas.toBlob(async (blob) => {
          await executeApiAction({
            setLoading: setSubmitting,
            dispatch,
            action: () => uploadPersonPhoto({ ...data, ptsPersonID: personId, file: blob }),
            onSuccess: () => {
              dispatch(notify('Snapshot uploaded successfully', 'success'));
              onSaved?.();
              clearPhoto();
            },
          });
          resolve();
        });
      });
    });

    const handleCancel = useCallback(() => onClose?.(), [onClose]);

    useEffect(() => {
      isMountedRef.current = true;
      startCamera();

      return () => {
        isMountedRef.current = false;
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };
    }, [startCamera]);

    const isIdle = !hasVideo && !hasPhoto;

    return (
      <Paper variant="outlined" className="person-webcam-capture">
        <Grid container spacing={2}>
          <Grid item xs={12} md={hasPhoto ? 4 : 7}>
            <video
              ref={videoRef}
              className="person-webcam-capture__video"
              autoPlay
              muted
              playsInline
            />

            {hasVideo && (
              <Box mt={1} className="person-webcam-capture__button-row">
                <RMSButton label="Take Snapshot" prefixIcon={<CameraIcon />} onClick={takePhoto} />
                <RMSButton
                  label="Stop Live Feed"
                  variant="outlined"
                  prefixIcon={<StopIcon />}
                  onClick={stopVideo}
                />
              </Box>
            )}

            {isIdle && (
              <Box mt={1}>
                <Typography variant="body2" color="textSecondary">
                  Live feed stopped.
                </Typography>
                <Box mt={1} className="person-webcam-capture__button-row">
                  <RMSButton
                    label="Restart Camera"
                    prefixIcon={<CameraIcon />}
                    onClick={startCamera}
                  />
                  <RMSButton
                    label="Cancel"
                    variant="outlined"
                    prefixIcon={<CloseIcon />}
                    onClick={handleCancel}
                  />
                </Box>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={hasPhoto ? 8 : 5}>
            <canvas
              ref={photoRef}
              className={clsx(
                'person-webcam-capture__canvas',
                hasPhoto && 'person-webcam-capture__canvas--filled'
              )}
            />

            {hasPhoto && (
              <FormProvider {...methods}>
                <Box mt={1}>
                  <PersonPhotoMetaFields
                    hasDefaultPhoto={hasDefaultPhoto}
                    forceMark={forceMark}
                    defaultToMarksType={forceMark}
                  />
                </Box>

                <Box mt={1} className="person-webcam-capture__button-row">
                  <RMSButton
                    label="Save Snapshot"
                    prefixIcon={<SaveIcon />}
                    onClick={saveSnap}
                    disabled={submitting || !canEditPerson}
                  />
                  <RMSButton
                    label="Clear Image"
                    variant="outlined"
                    prefixIcon={<ClearIcon />}
                    onClick={clearPhoto}
                  />
                </Box>
              </FormProvider>
            )}
          </Grid>
        </Grid>
      </Paper>
    );
  }
);

export default PersonWebcamCapture;
